// Pre-auths fixtures (PREAUTHS_HANDOFF §11). Copy from the boards.
import type { ApprovedItem, Billing, SentItem, Settings, ToSendItem } from './types';
import { PMS } from '@/features/shared/pms';

const IMPLANT = { code: 'D6010', description: 'Implant body, endosteal', fee: 2140 };

/**
 * The consequence line, worded by whether the patient can be billed (Oct 22, PREAUTHS_HANDOFF §6).
 * "may owe the full" is only for out of network; never for Medicaid/CHIP or in network.
 */
export function consequenceLine(args: { denier: string; first: string; billing: Billing; fee: number }): string {
  const { denier, first, billing, fee } = args;
  if (billing === 'barred') return `Without one, ${denier} will deny the claim, and ${first} can’t be billed for it.`;
  if (billing === 'in_network') {
    return `Without one, ${denier} will deny the claim, and your contract may not let you bill ${first} for it.`;
  }
  return `Without one, ${denier} will deny the claim, and ${first} may owe the full $${fee.toLocaleString('en-US')}.`;
}

export function buildToSend(): ToSendItem[] {
  return [
    // Teresa — Needs you (not enough time)
    {
      id: 'PA-T1',
      patient: 'Teresa Nguyen',
      firstName: 'Teresa',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '448-77-2016',
      provider: 'Dr. Emily Carter',
      procedures: [{ ...IMPLANT, tooth: '#19' }],
      procedureWords: 'Implant at #19',
      procedureGroup: 'implants',
      procedureGroupId: 'implants',
      billing: 'in_network',
      requirement: 'MetLife requires it for implants.',
      treatment: 2140,
      appointmentDate: '2026-10-21',
      appointmentDisplay: 'Oct 21, 2026, 10:00 AM',
      bookedDisplay: 'Oct 14',
      group: 'needs_you',
      reasonLine: 'Not enough time before the visit',
      why: {
        headline: 'MetLife requires a pre-auth for implants on Teresa’s plan',
        consequence: consequenceLine({ denier: 'MetLife', first: 'Teresa', billing: 'in_network', fee: 2140 }),
        sources: [
          { id: 'rule-metlife', label: 'MetLife rules: implants', detail: 'MetLife’s dental provider manual, §7.3 “Implant services”: a pre-authorization is required before D6010–D6067 on this plan.' },
          { id: 'plan-teresa', label: 'Teresa’s plan, checked Oct 14', detail: 'Teresa’s eligibility, checked with Stedi on Oct 14, 2026: implants covered at 50% once pre-authorized; $2,000 remaining this year.' },
          { id: 'hist-metlife', label: '18 MetLife pre-auths from your office', detail: 'Your office’s own history: 18 MetLife implant pre-auths in the last 24 months, 16 approved. No other patients’ names are shown.' },
        ],
      },
      decision: {
        title: 'MetLife may not answer before Oct 21',
        body:
          'MetLife answers in about 10 days, so the answer would come around Oct 25 — after the Oct 21 appointment.',
        meta:
          `Moving the appointment gives MetLife time to answer: your team moves it in ${PMS}, and ELVA re-checks the timing when it moves. Keeping Oct 21 means treating before the answer is back.`,
        options: [
          { id: 'move', label: 'Send now; we’ll move the appointment', style: 'secondary', effect: 'send_move_appt', resolvedText: 'send now; we’ll move the appointment' },
          { id: 'keep', label: 'Send now and keep Oct 21', style: 'secondary', effect: 'send', resolvedText: 'send and keep Oct 21' },
          { id: 'dont', label: 'Don’t send', style: 'ghost', effect: 'dont_send', resolvedText: 'don’t send' },
        ],
      },
      documents: [
        { id: 'd1', name: 'Panoramic X-ray', meta: `Oct 14, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
        { id: 'd2', name: 'CBCT scan of #19', meta: `Oct 14, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
        { id: 'd3', name: 'Surgical treatment plan', meta: 'From Dr. Carter’s note of Oct 14', status: 'ok', required: true },
      ],
      narrative: {
        text:
          'Pre-authorization requested for D6010, implant body, at site #19.\nTooth #19 was extracted in January 2026 because of a vertical root fracture. The panoramic X-ray and CBCT of October 14, 2026 show a healed site with adequate bone. An implant is the appropriate restoration; the adjacent teeth are sound.',
        sources: 'Dr. Carter’s note, the X-ray and the CBCT of Oct 14',
      },
      history: [`appointment booked in ${PMS} Oct 14`, 'ELVA prepared the pre-auth Oct 14'],
    },

    // Paul — Needs you (X-ray too old)
    {
      id: 'PA-T2',
      patient: 'Paul Reyes',
      firstName: 'Paul',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      memberId: 'W281-63-4410',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ ...IMPLANT, tooth: '#14' }],
      procedureWords: 'Implant at #14',
      procedureGroup: 'implants',
      procedureGroupId: 'implants',
      billing: 'in_network',
      requirement: 'Aetna requires it for implants.',
      treatment: 2140,
      appointmentDate: '2026-11-18',
      appointmentDisplay: 'Nov 18, 2026, 8:30 AM',
      bookedDisplay: 'Oct 12',
      group: 'needs_you',
      reasonLine: 'The latest X-ray is from 2023',
      why: {
        headline: 'Aetna requires a pre-auth for implants on Paul’s plan',
        consequence: consequenceLine({ denier: 'Aetna', first: 'Paul', billing: 'in_network', fee: 2140 }),
        timing: {
          hasTime: true,
          sentence:
            'Aetna usually answers in about 15 days. Paul’s visit is Nov 18, so an answer sent after the Oct 27 X-ray should come back in time.',
        },
        sources: [
          { id: 'rule-aetna', label: 'Aetna rules: implants', detail: 'Aetna’s dental office manual, “Predetermination of benefits”: implants (D6010) require a pre-authorization with a radiograph from the last 12 months.' },
          { id: 'plan-paul', label: 'Paul’s plan, checked Oct 12', detail: 'Paul’s eligibility, checked with Stedi on Oct 12, 2026: implants covered at 50% after pre-authorization; missing-tooth clause does not apply.' },
        ],
      },
      decision: {
        title: 'The latest X-ray of #14 is from 2023',
        body:
          'Aetna wants an X-ray from the last 12 months for an implant pre-auth. Paul’s next visit is a cleaning on Oct 27 — ELVA will add a note to the Oct 27 cleaning asking for one.',
        meta: 'Waiting for the new X-ray is stronger; the 2023 X-ray may be refused as too old. Nothing is sent until the X-ray is in.',
        options: [
          { id: 'take', label: 'Ask for an X-ray at the Oct 27 cleaning, then send', style: 'secondary', effect: 'send_wait_doc', resolvedText: 'ask for a new X-ray, then send' },
          { id: 'old', label: 'Send now with the 2023 X-ray', style: 'secondary', effect: 'send', resolvedText: 'send with the 2023 X-ray' },
          { id: 'dont', label: 'Don’t send', style: 'ghost', effect: 'dont_send', resolvedText: 'don’t send' },
        ],
      },
      documents: [
        { id: 'd1', name: 'Periapical X-ray of #14', meta: `Mar 2023 · from ${PMS} imaging`, status: 'too_old', required: true },
        { id: 'd2', name: 'Surgical treatment plan', meta: 'From Dr. Brooks’s note of Oct 12', status: 'ok', required: true },
        { id: 'd3', name: 'Intraoral photo of the site', meta: `Oct 12, 2026 · from ${PMS}`, status: 'ok', required: true },
      ],
      narrative: {
        text:
          'Pre-authorization requested for D6010, implant body, at site #14.\nTooth #14 was lost to non-restorable decay. The surgical treatment plan and intraoral photo support an implant restoration. A current periapical radiograph will be provided from the October 27 visit.',
        sources: 'Dr. Brooks’s note and the treatment plan of Oct 12',
      },
      history: [`appointment booked in ${PMS} Oct 12`, 'ELVA prepared the pre-auth Oct 12'],
    },

    // Daniel — Ready (board 1)
    {
      id: 'PA-T3',
      patient: 'Daniel Foster',
      firstName: 'Daniel',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      memberId: 'U77-402-118',
      provider: 'Dr. Olivia Brooks',
      procedures: [{ ...IMPLANT, tooth: '#30' }],
      procedureWords: 'Implant at #30',
      procedureGroup: 'implants',
      procedureGroupId: 'implants',
      billing: 'out_of_network',
      requirement: 'Cigna requires it for implants.',
      treatment: 2140,
      appointmentDate: '2026-11-12',
      appointmentDisplay: 'Nov 12, 2026, 9:00 AM',
      bookedDisplay: 'Oct 9',
      group: 'ready',
      reasonLine: 'Cigna requires it for implants',
      why: {
        headline: 'Cigna requires a pre-auth for implants on Daniel’s plan',
        consequence: consequenceLine({ denier: 'Cigna', first: 'Daniel', billing: 'out_of_network', fee: 2140 }),
        timing: {
          hasTime: true,
          sentence:
            'Cigna usually answers in about 14 days, so the answer should come around Oct 29 — two weeks before the appointment.',
        },
        sources: [
          { id: 'rule-cigna', label: 'Cigna rules: implants', detail: 'Cigna’s dental provider manual, “Predetermination”: implants (D6010) require a pre-authorization on DPPO plans.' },
          { id: 'plan-daniel', label: 'Daniel’s plan, checked Oct 9', detail: 'Daniel’s eligibility, checked with Stedi on Oct 9, 2026: implants covered at 50% once pre-authorized; $2,100 remaining this year.' },
          { id: 'hist-cigna', label: '31 Cigna pre-auths from your office', detail: 'Your office’s own history: 31 Cigna implant pre-auths in the last 24 months, 29 approved in about 14 days. No other patients’ names are shown.' },
        ],
      },
      documents: [
        { id: 'd1', name: 'Panoramic X-ray', meta: `Oct 9, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
        { id: 'd2', name: 'CBCT scan of #30', meta: `Oct 9, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
        { id: 'd3', name: 'Surgical treatment plan', meta: 'From Dr. Brooks’s note of Oct 9', status: 'ok', required: true },
        { id: 'd4', name: 'Intraoral photo of the site', meta: `Oct 9, 2026 · from ${PMS}`, status: 'ok', required: true },
      ],
      narrative: {
        text:
          'Pre-authorization requested for D6010, implant body, at site #30.\nTooth #30 was extracted on February 14, 2026 because of non-restorable decay. The panoramic X-ray and CBCT of October 9, 2026 show a healed site with adequate bone height and width. Teeth #29 and #31 are sound, so a fixed bridge would mean crowning two healthy teeth. Mr. Foster has declined a removable partial denture. An implant is the appropriate restoration.',
        sources: 'Dr. Brooks’s note, the X-rays and the CBCT of Oct 9',
      },
      history: [`appointment booked in ${PMS} Oct 9`, 'ELVA prepared the pre-auth Oct 9'],
    },

    // Lucas — Needs you (waiting period caught before sending, T4, Oct 18 reseed)
    {
      id: 'PA-T4',
      patient: 'Lucas Bennett',
      firstName: 'Lucas',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '552-19-8834',
      provider: 'Dr. Emily Carter',
      procedures: [{ ...IMPLANT, tooth: '#8' }],
      procedureWords: 'Implant at #8',
      procedureGroup: 'implants',
      procedureGroupId: 'implants',
      billing: 'in_network',
      requirement: 'MetLife requires it for implants.',
      treatment: 2140,
      appointmentDate: '2026-12-02',
      appointmentDisplay: 'Dec 2, 2026, 11:00 AM',
      bookedDisplay: 'Oct 13',
      group: 'needs_you',
      reasonLine: 'MetLife’s 12-month waiting period hasn’t ended',
      why: {
        headline: 'MetLife requires a pre-auth for implants on Lucas’s plan',
        consequence: consequenceLine({ denier: 'MetLife', first: 'Lucas', billing: 'in_network', fee: 2140 }),
        sources: [
          { id: 'rule-metlife-lucas', label: 'MetLife rules: implants', detail: 'MetLife’s dental provider manual, §7.3 “Implant services”: a pre-authorization is required before D6010–D6067 on this plan.' },
          { id: 'plan-lucas', label: 'Lucas’s plan, checked Oct 13', detail: 'Lucas’s eligibility, checked with Stedi on Oct 13, 2026: implants covered at 50% once pre-authorized, but subject to a 12-month waiting period; coverage started Mar 2, 2026.' },
        ],
      },
      decision: {
        title: 'Lucas’s plan has a waiting period that hasn’t ended',
        body:
          'MetLife dental plans wait 12 months before covering implants. Lucas’s coverage started Mar 2, 2026, so the waiting period ends Mar 2, 2027 — after the Dec 2 visit. ELVA caught this before sending.',
        meta: 'Sending now risks a denial that still counts against your office’s history with MetLife. Waiting until the period ends avoids it; sending anyway is your call.',
        options: [
          { id: 'send', label: 'Send now anyway', style: 'secondary', effect: 'send', resolvedText: 'send now anyway' },
          { id: 'wait', label: 'Wait until Mar 2, 2027, then send', style: 'secondary', effect: 'dont_send', resolvedText: 'wait until Mar 2, 2027' },
          { id: 'dont', label: 'Don’t send', style: 'ghost', effect: 'dont_send', resolvedText: 'don’t send' },
        ],
      },
      documents: [
        { id: 'd1', name: 'Panoramic X-ray', meta: `Oct 13, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
        { id: 'd2', name: 'Surgical treatment plan', meta: 'From Dr. Carter’s note of Oct 13', status: 'ok', required: true },
      ],
      narrative: {
        text:
          'Pre-authorization requested for D6010, implant body, at site #8.\nTooth #8 was extracted in 2025 because of a vertical root fracture. The panoramic X-ray shows a healed site with adequate bone. An implant is the appropriate restoration.',
        sources: 'Dr. Carter’s note and the X-ray of Oct 13',
      },
      history: [`appointment booked in ${PMS} Oct 13`, 'ELVA prepared the pre-auth Oct 13', 'ELVA found a waiting-period restriction Oct 13'],
    },
  ];
}

/** `?scenario=medicaid`: a Texas Medicaid crown the patient can't be billed for (Oct 22, billing 'barred'). */
export function buildMedicaidItem(): ToSendItem {
  const fee = 1295; // D2740, claims fee schedule (CLAIMS_UI_BUILD_SPEC §14.1)
  return {
    id: 'PA-T5',
    patient: 'Marcus Hill',
    firstName: 'Marcus',
    payer: { name: 'Texas Medicaid', short: 'Texas Medicaid' },
    memberId: '7201 4418 62',
    provider: 'Dr. Emily Carter',
    procedures: [{ code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#3', fee }],
    procedureWords: 'Crown at #3',
    procedureGroup: 'crowns',
    procedureGroupId: 'crowns',
    billing: 'barred',
    denier: 'DentaQuest',
    requirement: 'Texas Medicaid requires prior authorization for crowns.',
    treatment: fee,
    appointmentDate: '2026-11-20',
    appointmentDisplay: 'Nov 20, 2026, 1:30 PM',
    bookedDisplay: 'Oct 13',
    group: 'ready',
    reasonLine: 'Texas Medicaid requires it for crowns',
    why: {
      headline: 'Texas Medicaid requires a pre-auth for crowns on Marcus’s plan',
      consequence: consequenceLine({ denier: 'DentaQuest', first: 'Marcus', billing: 'barred', fee }),
      timing: {
        hasTime: true,
        sentence: 'DentaQuest usually answers in about 14 days, so the answer should come around Oct 29, well before the appointment.',
      },
      sources: [
        { id: 'rule-medicaid', label: 'Texas Medicaid rules: crowns', detail: 'DentaQuest’s Texas Medicaid manual: prior authorization is required for crowns (D2710–D2794).' },
        { id: 'plan-marcus', label: 'Marcus’s plan, checked Oct 13', detail: 'Marcus’s eligibility, checked with Stedi on Oct 13, 2026: Texas Medicaid through DentaQuest, active.' },
      ],
    },
    documents: [
      { id: 'd1', name: 'Periapical X-ray of #3', meta: `Oct 13, 2026 · from ${PMS} imaging`, status: 'ok', required: true },
      { id: 'd2', name: 'Treatment plan', meta: 'From Dr. Carter’s note of Oct 13', status: 'ok', required: true },
    ],
    narrative: {
      text:
        'Prior authorization requested for D2740, crown, at tooth #3.\nTooth #3 has a large fractured restoration with recurrent decay that leaves too little tooth structure for a direct restoration. The periapical X-ray of October 13, 2026 shows no periapical pathology. A full-coverage crown is the appropriate restoration.',
      sources: 'Dr. Carter’s note and the X-ray of Oct 13',
    },
    history: [`appointment booked in ${PMS} Oct 13`, 'ELVA prepared the pre-auth Oct 13'],
  };
}

export function buildSent(): SentItem[] {
  return [
    {
      id: 'PA-S1',
      patient: 'Jorge Silva',
      payerShort: 'MetLife',
      code: 'D6010',
      procedure: 'implant #3',
      sentDisplay: 'Sep 29',
      appointmentDisplay: 'Oct 30',
      status: 'denied',
      sentence:
        'MetLife said no on Oct 12 — tooth #3 was missing before Jorge’s coverage started, a missing-tooth clause ELVA couldn’t see in advance.',
      action: 'decide',
      denyReason: 'missing_tooth',
      reference: 'MET-PA-55902',
      timeline: [
        { when: 'Sep 29', text: 'ELVA sent the pre-auth to MetLife with the panoramic X-ray and treatment plan.' },
        { when: 'Oct 2', text: 'MetLife acknowledged receipt, reference MET-PA-55902.' },
        { when: 'Oct 12', text: 'MetLife denied it: tooth #3 was missing before Jorge’s coverage started — a missing-tooth clause ELVA had no way to see in advance.' },
      ],
    },
    {
      id: 'PA-S2',
      patient: 'Olivia Grant',
      payerShort: 'Delta',
      code: 'D6010',
      procedure: 'implant #30',
      sentDisplay: 'Sep 24',
      appointmentDisplay: 'Nov 9',
      status: 'asked_more',
      sentence: 'Delta asked for a periodontal chart on Oct 10. ELVA attached the chart from Sep 30.',
      action: 'review_resend',
      reference: 'DEL-PA-30881',
      timeline: [
        { when: 'Sep 24', text: 'ELVA sent the pre-auth to Delta with the X-rays and treatment plan.' },
        { when: 'Oct 1', text: 'Delta acknowledged receipt, reference DEL-PA-30881.' },
        { when: 'Oct 10', text: 'Delta asked for a periodontal chart. ELVA found and attached Olivia’s chart from Sep 30.' },
      ],
    },
    {
      id: 'PA-S3',
      patient: 'Karen Ross',
      payerShort: 'Aetna',
      code: 'D6010',
      procedure: 'implant #19',
      sentDisplay: 'Oct 2',
      appointmentDisplay: 'Oct 28',
      status: 'late',
      sentence:
        'Aetna should have answered by Oct 14. ELVA checked today: still in review, reference AAH-771230. ELVA checks again on Oct 19.',
      reference: 'AAH-771230',
      timeline: [
        { when: 'Oct 2', text: 'ELVA sent the pre-auth to Aetna with the X-rays and treatment plan.' },
        { when: 'Oct 3', text: 'Aetna acknowledged receipt, reference AAH-771230.' },
        { when: 'Oct 15', text: 'Aetna should have answered by Oct 14. ELVA checked: still in review. It checks again Oct 19.' },
      ],
    },
    {
      id: 'PA-S4',
      patient: 'Steven Moore',
      payerShort: 'Cigna',
      code: 'D6010',
      procedure: 'implant #3',
      sentDisplay: 'Oct 8',
      appointmentDisplay: 'Nov 5',
      status: 'waiting',
      sentence: 'Cigna usually answers in about 14 days — by Oct 22.',
      reference: 'CIG-PA-41007',
      timeline: [
        { when: 'Oct 8', text: 'ELVA sent the pre-auth to Cigna with the X-rays and treatment plan.' },
        { when: 'Oct 9', text: 'Cigna acknowledged receipt, reference CIG-PA-41007. It usually answers in about 14 days — by Oct 22.' },
      ],
    },
  ];
}

export function buildApproved(): ApprovedItem[] {
  return [
    {
      id: 'PA-A1',
      patient: 'Brian Cole',
      payerShort: 'Cigna',
      code: 'D6010',
      procedure: 'implant #19',
      reference: 'CIG-PA-22134',
      estimate: 1070,
      validUntil: 'Nov 18',
      appointmentDisplay: 'Nov 24',
      state: 'expires_first',
      sentence:
        'The pre-auth runs out 6 days before the visit. Move the visit before Nov 18, or a new pre-auth will be needed.',
    },
    {
      id: 'PA-A2',
      patient: 'Ana Torres',
      payerShort: 'Aetna',
      code: 'D6010',
      procedure: 'implant #14',
      reference: 'AET-PA-77821',
      estimate: 1070,
      validUntil: 'Dec 29',
      appointmentDisplay: 'Not booked',
      state: 'not_booked',
      sentence: 'No visit booked. Your team sent this one. Book the visit before Dec 29 to use it.',
    },
    {
      id: 'PA-A3',
      patient: 'Sarah Mitchell',
      payerShort: 'Delta',
      code: 'D6010',
      procedure: 'implant #30',
      reference: 'PA-3299',
      estimate: 1610,
      validUntil: 'Mar 18, 2027',
      appointmentDisplay: 'Done Oct 6',
      state: 'used',
      sentence: 'ELVA added PA-3299 to the claim for Oct 6.',
    },
    {
      id: 'PA-A4',
      patient: 'Marcus Webb',
      payerShort: 'MetLife',
      code: 'D6010',
      procedure: 'implant #8',
      reference: 'MET-PA-60142',
      estimate: 1070,
      validUntil: 'Jan 20, 2027',
      appointmentDisplay: 'Visit cancelled',
      state: 'cancelled',
      sentence:
        'The Oct 30 visit was cancelled after this was approved. ELVA keeps it — if the implant is rebooked before Jan 20, 2027, it attaches to that visit automatically. ELVA never withdraws it from MetLife.',
    },
  ];
}

export function buildSettings(): Settings {
  return {
    autopilot: false,
    onboardingDate: 'Oct 1, 2026',
    payers: [
      {
        id: 'texas_medicaid',
        name: 'Texas Medicaid',
        short: 'Texas Medicaid',
        adminThrough: 'through DentaQuest',
        patients: 140,
        requiresWords: 'Crowns, molar root canals, scaling and root planing, dentures',
        source: 'From DentaQuest’s Texas manual',
        prepare: true,
        procedures: [
          { id: 'crowns', name: 'Crowns', codes: 'D2710–D2794', on: true, setBy: 'Set during onboarding' },
          { id: 'molar_rct', name: 'Molar root canals', codes: 'D3330', on: true, setBy: 'Set during onboarding' },
          { id: 'srp', name: 'Scaling and root planing', codes: 'D4341, D4342', on: false, setBy: 'Turned off by Ava Thompson · Oct 9' },
          { id: 'dentures', name: 'Dentures', codes: 'D5110–D5214', on: true, setBy: 'Set during onboarding' },
        ],
      },
      {
        id: 'cigna',
        name: 'Cigna',
        short: 'Cigna',
        patients: 96,
        requiresWords: 'Implants',
        source: 'From Cigna’s provider manual',
        prepare: true,
        procedures: [{ id: 'implants', name: 'Implants', codes: 'D6010', on: true, setBy: 'Set during onboarding' }],
      },
      {
        id: 'aetna',
        name: 'Aetna PPO',
        short: 'Aetna',
        patients: 88,
        requiresWords: 'Implants, gum surgery',
        source: 'From Aetna’s provider manual',
        prepare: true,
        procedures: [
          { id: 'implants', name: 'Implants', codes: 'D6010', on: true, setBy: 'Set during onboarding' },
          { id: 'gum', name: 'Gum surgery', codes: 'D4260–D4261', on: true, setBy: 'Set during onboarding' },
        ],
      },
      {
        id: 'metlife',
        name: 'MetLife',
        short: 'MetLife',
        patients: 72,
        requiresWords: 'Implants',
        source: 'From your office’s history: MetLife asked for a pre-auth on 18 implants',
        notInManual: true,
        prepare: true,
        procedures: [{ id: 'implants', name: 'Implants', codes: 'D6010', on: true, setBy: 'Set during onboarding' }],
      },
      {
        id: 'delta',
        name: 'Delta Dental PPO',
        short: 'Delta',
        patients: 60,
        requiresWords: 'Implants',
        source: 'From Delta’s provider manual',
        prepare: false,
        prepareChangedBy: 'Turned off by Ava Thompson · Oct 9',
        procedures: [{ id: 'implants', name: 'Implants', codes: 'D6010', on: true, setBy: 'Set during onboarding' }],
      },
      {
        id: 'guardian',
        name: 'Guardian',
        short: 'Guardian',
        patients: 24,
        requiresWords: '',
        source: '',
        nothingFound: true,
        prepare: false,
        procedures: [],
      },
    ],
  };
}
